import React, { useState } from 'react';
import { Box, Button, Collapse, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography, alpha, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Reconciliation } from '../../../../types/finance';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatStatementPaisa } from '../../../../utils/paisa';
import { ToneChip } from '../../../common/StatusChip';
import ReconciliationSummary from './ReconciliationSummary';
import ReconciliationDownload from './ReconciliationDownload';
import ReconciliationLinesTable from './ReconciliationLinesTable';
import { reconciliationStatement } from './reconciliationStatement';

/**
 * A completed reconciliation: the statement balance carried to the books
 * through what was still outstanding on that date, with the lines behind
 * each figure.
 */
const ReconciliationReport: React.FC<{ rec: Reconciliation; onOpenEntry: (entryId: string) => void }> = ({ rec, onOpenEntry }) => {
  const theme = useTheme();
  const { formatDate, formatDateTime } = useTimezone();
  const [showCleared, setShowCleared] = useState(false);
  const s = rec.summary;
  const outstanding = rec.lines.filter(l => !l.cleared);
  const cleared = rec.lines.filter(l => l.cleared);
  const ties = s.statementBalancePaisa + s.depositsInTransitPaisa - s.unpresentedPaymentsPaisa === s.bookBalancePaisa;

  const rows = reconciliationStatement(rec, formatDate);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 500, mr: 1 }}>{rec.accountCode}</Box>
            {rec.accountName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reconciled to the statement of {formatDate(rec.statementDate)}
            {rec.completedAt && <> · completed {formatDateTime(rec.completedAt)}{rec.completedByName && <> by {rec.completedByName}</>}</>}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToneChip tone="success" label="Completed" />
          <ReconciliationDownload rec={rec} />
        </Stack>
      </Stack>

      <ReconciliationSummary rec={rec} />

      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
          <Typography variant="subtitle2" fontWeight={700}>Bank reconciliation statement</Typography>
        </Box>
        <Table size="small">
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.label} sx={r.kind === 'total' ? {
                '& td': { borderTop: `2px solid ${theme.palette.text.primary}`, borderBottom: `3px double ${theme.palette.text.primary}` },
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              } : undefined}>
                <TableCell>
                  <Typography variant="body2" fontWeight={r.kind === 'total' ? 800 : 400}>{r.label}</Typography>
                  {r.hint && <Typography variant="caption" color="text.secondary">{r.hint}</Typography>}
                </TableCell>
                <TableCell align="right" sx={{ width: 200 }}>
                  <Typography variant="body2" fontWeight={r.kind === 'total' ? 800 : 500} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatStatementPaisa(r.paisa)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!ties && (
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', px: 2, py: 1 }}>
            These figures don't tie to the books — an earlier reconciliation of this account may be off.
          </Typography>
        )}
      </Paper>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>Outstanding on {formatDate(rec.statementDate)}</Typography>
      <ReconciliationLinesTable lines={outstanding} editable={false} onOpenEntry={onOpenEntry} emptyText="Every line was on the statement." />

      <Button size="small" sx={{ mt: 2 }} onClick={() => setShowCleared(v => !v)} endIcon={showCleared ? <ExpandLess /> : <ExpandMore />}>
        {showCleared ? 'Hide' : 'Show'} the {cleared.length} cleared line{cleared.length === 1 ? '' : 's'}
      </Button>
      <Collapse in={showCleared} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <ReconciliationLinesTable lines={cleared} editable={false} onOpenEntry={onOpenEntry} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ReconciliationReport;
